import { getLocale } from '@/helpers';
import { logHelper } from '@/helpers/log-helper';
import { logger } from '@/helpers/log.service';
import { messageService } from '@/helpers/message.service';
import { isNativeAndroid, isNativeIos } from '@/helpers/platform-helper';

enum CordovaFilePluginError {
  NOT_FOUND_ERR = 1,
  SECURITY_ERR = 2,
  ABORT_ERR = 3,
  NOT_READABLE_ERR = 4,
  ENCODING_ERR = 5,
  NO_MODIFICATION_ALLOWED_ERR = 6,
  INVALID_STATE_ERR = 7,
  SYNTAX_ERR = 8,
  INVALID_MODIFICATION_ERR = 9,
  QUOTA_EXCEEDED_ERR = 10,
  TYPE_MISMATCH_ERR = 11,
  PATH_EXISTS_ERR = 12
}

async function readDirectory(dir: any) {
  return new Promise((resolve, reject) => {
    try {
      const reader = dir.createReader();
      reader.readEntries((entries) => resolve(entries), reject);
    } catch (error) {
      reject(error);
    }
  });
}

async function getNewFileNameAndroid(dir: any, fileName: string): Promise<string> {
  let newFileName = fileName;
  const dirEntries = (await readDirectory(dir)) as Array<any>;

  const fn = fileName.substring(0, fileName.lastIndexOf('.'));
  const ext = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
  const regExp = new RegExp(`^${fn}(\\s\\(\\d+\\))?\\.${ext}`);

  const fileNameCount = dirEntries.filter(e => e.isFile && regExp.test(e.name)).length;
  if (fileNameCount) {
    newFileName = `${fn} (${fileNameCount}).${ext}`;
  }
  return newFileName;
}

async function getNewFileNameIos(dir: any, fileName: string): Promise<string> {
  let newFileName = fileName;
  const dirEntries = (await readDirectory(dir)) as Array<any>;
  const files = dirEntries.filter(e => e.isFile);

  if (files.length) {
    const fn = fileName.substring(0, fileName.lastIndexOf('.'));
    const ext = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
    let sameNameCounter = 0;
    let continueSearchName;
    do {
      continueSearchName = false;
      for (let i = 0; i < files.length; i++) {
        if (files[i].name.localeCompare(newFileName) === 0) {
          sameNameCounter++;
          newFileName = `${fn} (${sameNameCounter}).${ext}`;
          continueSearchName = true;
          break;
        }
      }
    } while (continueSearchName);
  }
  return newFileName;
}

function resolveLocalFileSystemURL(storageLocation: string) {
  return new Promise((resolve, reject) => {
    (window as any).resolveLocalFileSystemURL(storageLocation, resolve, reject);
  });
}

function createFile(dir: any, fileName: string, exclusive: boolean = false) {
  return new Promise((resolve, reject) => {
    dir.getFile(fileName, { create: true, exclusive }, resolve, reject);
  });
}

function createFileWriter(file: any): Promise<any> {
  return new Promise((resolve, reject) => {
    file.createWriter(resolve, reject);
  });
}

async function writeFile(file: any, blob: Blob): Promise<void> {
  const fileWriter = await createFileWriter(file);

  return new Promise((resolve, reject) => {
    fileWriter.onwriteend = (e) => {
      if (e.target.error) {
        reject(e.target.error);
      } else {
        resolve();
      }
    };
    fileWriter.onerror = (err) => {
      reject(err);
    };
    fileWriter.write(blob);
  });
}

function showSavedSuccessfullyMessage(fileName: string) {
  messageService.success({
    message: getLocale('fileSave.saved',
      {
        file: fileName,
        folder: isNativeIos() ? 'AskodOnline' : getLocale('fileSave.downloadFolder').toString()
      }
    ).toString()
  });
}

async function getFileSavingDirectory(): Promise<any> {
  let storageLocation = '';
  const _window = (window as any);
  const device = _window.device;
  const cordova = _window.cordova;

  switch (device.platform) {
    case 'Android':
      storageLocation = `${cordova.file.externalRootDirectory}Download`;
      break;
    case 'iOS':
      storageLocation = cordova.file.documentsDirectory;
      break;
  }

  logger.debug(`[FileSystemNativeService:getFileSavingDirectory] attempt to resolve storageLocation=${storageLocation}`);
  const dir = await resolveLocalFileSystemURL(storageLocation);

  return dir;
}

async function saveFileToDirectory(fileName: string, fileBlob: Blob, dir: any): Promise<string> {
  return isNativeIos()
    ? saveFileToDirectoryIos(fileName, fileBlob, dir)
    : saveFileToDirectoryAndroid(fileName, fileBlob, dir);
}

async function saveFileToDirectoryIos(fileName: string, fileBlob: Blob, dir: any): Promise<string> {
  const newFileName = await getNewFileNameIos(dir, fileName);
  logger.debug(`[FileSystemNativeService:saveFileToDirectoryIos] got new file name`, newFileName);

  const file = await createFile(dir, newFileName);
  logger.debug(`[FileSystemNativeService:saveFileToDirectoryIos] got file`, file);

  await writeFile(file, fileBlob);
  logger.debug(`[FileSystemNativeService:saveFileToDirectoryIos] file "${newFileName}" wrote to fs`);

  return newFileName;
}

function buildFileName(fileName: string, fileExt: string, nameCounter: number): string {
  return nameCounter
    ? `${fileName} (${nameCounter}).${fileExt}`
    : `${fileName}.${fileExt}`;
}

async function tryToCreateFileAndroid(fileName: string, dir: any): Promise<any> {
  const fn = fileName.substring(0, fileName.lastIndexOf('.'));
  const ext = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
  logger.debug(`[FileSystemNativeService:tryToCreateFileAndroid] fn="${fn}" ext="${ext}"`);

  let file;
  let newFileName;
  let nameCounter = 0;
  while (!file) {
    newFileName = buildFileName(fn, ext, nameCounter);
    try {
      logger.debug(`[FileSystemNativeService:tryToCreateFileAndroid] creating file "${newFileName}"`);
      file = await createFile(dir, newFileName, true);
    } catch (err) {
      logger.debug(`[FileSystemNativeService:tryToCreateFileAndroid] error occured while creating file "${newFileName}"`, err);
      if (err && err.code === CordovaFilePluginError.PATH_EXISTS_ERR) {
        nameCounter++;
      } else {
        throw err;
      }
    }
  }
  return { file, newFileName };
}

async function saveFileToDirectoryAndroid(fileName: string, fileBlob: Blob, dir: any): Promise<string> {
  logger.debug(`[FileSystemNativeService:saveFileToDirectoryAndroid] fileName="${fileName}"`, dir);
  let { file, newFileName } = await tryToCreateFileAndroid(fileName, dir);

  await writeFile(file, fileBlob);

  return newFileName;
}

class FileSystemNativeService {
  public async saveFile(fileName: string, fileBlob: Blob): Promise<void> {
    logger.debug(`[FileSystemNativeService:saveFile] fileName=${fileName} blobSize=${fileBlob ? fileBlob.size : 0}`);

    try {
      const fileSavingDir = await getFileSavingDirectory();
      logger.debug(`[FileSystemNativeService:saveFile] file saving directory`, fileSavingDir);

      const savedFileName = await saveFileToDirectory(fileName, fileBlob, fileSavingDir);
      logger.debug(`[FileSystemNativeService:saveFile] file saved with name: "${savedFileName}"`);

      showSavedSuccessfullyMessage(savedFileName);
    } catch (err) {
      logHelper.error(err);
    }
  }
}

export default new FileSystemNativeService();
